import { useEffect, useState, useCallback, type ReactNode } from 'react';
import { api, ApiError } from '../lib/api';
import type { CampusView, InstitutionView } from '../lib/types';

const TYPE_LABEL: Record<string, string> = {
  School: 'School (K-12)',
  PuCollege: 'PU College',
  EngineeringCollege: 'Engineering College',
  DegreeCollege: 'Degree College (BCA, BCom, BSc, MCA…)',
  CoachingInstitute: 'Coaching Institute (UPSC, KAS, NEET…)',
  TrainingAcademy: 'Training Academy (Java, cyber security…)',
  Other: 'Other',
};

export function InstitutionsPage({ embedded = false }: { embedded?: boolean }) {
  const [campuses, setCampuses] = useState<CampusView[] | null>(null);
  const [institutions, setInstitutions] = useState<InstitutionView[]>([]);
  const [loading, setLoading] = useState(true);
  const [addingCampus, setAddingCampus] = useState(false);
  const [addInstFor, setAddInstFor] = useState<CampusView | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, i] = await Promise.all([
        api.get<CampusView[]>('/api/campuses'),
        api.get<InstitutionView[]>('/api/institutions'),
      ]);
      setCampuses(c);
      setInstitutions(i);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', marginBottom: 22, gap: 16 }}>
        <div>
          {!embedded && <div className="eyebrow" style={{ marginBottom: 6 }}>Structure</div>}
          {!embedded && <h1 className="h-display" style={{ fontSize: 28 }}>Campuses &amp; institutions</h1>}
          <p style={{ color: 'var(--ink-2)', marginTop: embedded ? 0 : 4 }}>The trust’s campuses and the institutions running at each.</p>
        </div>
        <button className="btn btn-accent" onClick={() => setAddingCampus(true)}>+ Add campus</button>
      </div>

      {loading && <p style={{ color: 'var(--ink-3)' }}>Loading…</p>}

      {!loading && campuses?.length === 0 && (
        <div className="card" style={{ padding: 32, textAlign: 'center' }}>
          <p style={{ color: 'var(--ink-2)', marginBottom: 16 }}>No campuses yet. Add the first one to start structuring the trust.</p>
          <button className="btn btn-primary" onClick={() => setAddingCampus(true)}>Add campus</button>
        </div>
      )}

      <div style={{ display: 'grid', gap: 16 }}>
        {campuses?.map((campus) => {
          const insts = institutions.filter((i) => i.campusId === campus.id);
          return (
            <div key={campus.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: insts.length ? 16 : 0 }}>
                <div>
                  <div className="h-display" style={{ fontSize: 18 }}>{campus.name}</div>
                  {campus.city && <div style={{ fontSize: 13, color: 'var(--ink-3)' }}>{campus.city}</div>}
                </div>
                <button className="btn" onClick={() => setAddInstFor(campus)}>+ Institution</button>
              </div>

              {insts.length > 0 && (
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 10 }}>
                  {insts.map((inst) => (
                    <div key={inst.id} style={{
                      border: '1px solid var(--line)', borderRadius: 'var(--r-md)', padding: '12px 14px',
                      background: 'var(--surface-2)',
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 14 }}>{inst.name}</div>
                      <div className="eyebrow" style={{ marginTop: 4, color: 'var(--accent-ink)' }}>
                        {TYPE_LABEL[inst.type] ?? inst.type}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {addingCampus && <CampusDialog onClose={() => setAddingCampus(false)} onDone={() => { setAddingCampus(false); load(); }} />}
      {addInstFor && <InstitutionDialog campus={addInstFor} onClose={() => setAddInstFor(null)} onDone={() => { setAddInstFor(null); load(); }} />}
    </div>
  );
}

function CampusDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [city, setCity] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try { await api.post('/api/campuses', { name: name.trim(), city: city.trim() || null }); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not add the campus.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title="Add campus" onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}
      <label className="label">Campus name</label>
      <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Jayanagar Campus" style={{ marginBottom: 14 }}
        onKeyDown={(e) => e.key === 'Enter' && name && submit()} />
      <label className="label">City (optional)</label>
      <input className="input" value={city} onChange={(e) => setCity(e.target.value)}
        placeholder="Bengaluru" style={{ marginBottom: 20 }} />
      <DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add campus" busy={busy} ready={!!name.trim()} />
    </Modal>
  );
}

function InstitutionDialog({ campus, onClose, onDone }: { campus: CampusView; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('School');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    setError(null); setBusy(true);
    try { await api.post('/api/institutions', { campusId: campus.id, name: name.trim(), type }); onDone(); }
    catch (e) { setError(e instanceof ApiError ? e.message : 'Could not add the institution.'); }
    finally { setBusy(false); }
  };

  return (
    <Modal title={`Add institution — ${campus.name}`} onClose={onClose}>
      {error && <ErrorBar>{error}</ErrorBar>}
      <label className="label">Institution name</label>
      <input className="input" autoFocus value={name} onChange={(e) => setName(e.target.value)}
        placeholder="e.g. Greenwood High School" style={{ marginBottom: 14 }}
        onKeyDown={(e) => e.key === 'Enter' && name && submit()} />
      <label className="label">Academic model</label>
      <select className="input" value={type} onChange={(e) => setType(e.target.value)} style={{ marginBottom: 8 }}>
        <option value="School">School (K-12)</option>
        <option value="PuCollege">PU College</option>
        <option value="EngineeringCollege">Engineering College</option>
        <option value="DegreeCollege">Degree College (BCA, BCom, BSc, MCA, MCom, BBM…)</option>
        <option value="CoachingInstitute">Coaching Institute (UPSC, KAS, NEET, banking…)</option>
        <option value="TrainingAcademy">Training Academy (Java, cyber security, data science…)</option>
        <option value="Other">Other</option>
      </select>
      <p style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 20 }}>
        This sets how students are organised: classes and sections for a school, branch and semester for a college, or named BATCHES for coaching and training institutes. Every other feature — fees, attendance, exams, staff — is the same whichever you pick.
      </p>
      <DialogActions onCancel={onClose} onConfirm={submit} confirmLabel="Add institution" busy={busy} ready={!!name.trim()} />
    </Modal>
  );
}

// ---- small shared modal pieces (local to this page) ----
export function Modal({ title, onClose, children }: { title: string; onClose: () => void; children: ReactNode }) {
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(10,12,18,.45)', zIndex: 50, display: 'grid', placeItems: 'center', padding: 20 }}>
      <div onClick={(e) => e.stopPropagation()} className="card" style={{ width: 420, maxWidth: '100%', padding: 24 }}>
        <h2 className="h-display" style={{ fontSize: 20, marginBottom: 18 }}>{title}</h2>
        {children}
      </div>
    </div>
  );
}
export function ErrorBar({ children }: { children: ReactNode }) {
  return <div className="pill pill-danger" style={{ padding: '8px 12px', borderRadius: 'var(--r-md)', justifyContent: 'flex-start', marginBottom: 16, width: '100%' }}>{children}</div>;
}
export function DialogActions({ onCancel, onConfirm, confirmLabel, busy, ready }: { onCancel: () => void; onConfirm: () => void; confirmLabel: string; busy: boolean; ready: boolean }) {
  return (
    <div style={{ display: 'flex', gap: 10 }}>
      <button className="btn" style={{ flex: 1, justifyContent: 'center' }} onClick={onCancel}>Cancel</button>
      <button className="btn btn-primary" style={{ flex: 1, justifyContent: 'center' }} disabled={!ready || busy} onClick={onConfirm}>
        {busy ? 'Saving…' : confirmLabel}
      </button>
    </div>
  );
}
